import { defineStore } from "pinia";
import { CommonEntityRecord } from "@/models/interfaces/common";
import {PersonApi} from "@/ api/PersonApi";
import { TagsApi } from "@/ api/TagsApi";
import { TagEntityType } from "@/models/etc/types";
import { RequestAddressContainer } from "@/composables/useAddressRequest";

export type ContactsState = {
    isClientsLoading: boolean,
    isClientsPartialLoading: boolean,
    canLoadNext: boolean,
    clients: Client[],
    editedClient: Client | number | null
};

export type ClientRequestData = {
    searchStr: string,
    inputContainer?: RequestAddressContainer | null,
    tags?: number[] | null,
    count: number,
    toSkip: number
}

export type Tag = {
    Id: number,
    Name: string,
    Type: number
}

export type AddressChainLevel = {
    Level: number,
    Name: string,
    InBriefDisplay: boolean
}


export type Client = {
    Id: number,
    Name: string
    Post: string,
    Contacts: string,
    Clients: CommonEntityRecord[],
    Tags: Tag[],
    Changeable: boolean,
    MainInProject: boolean,
    AddressChain: AddressChainLevel[],
    CreateTime: string,
}

export type AnalyticsListItem = {
    Position: number,
    Id: number,
    Name: string,
}

export type ChangelogItem = {
    AddressChanged: boolean
    ClientsChanged: boolean,
    ContactsChanged: boolean,
    NameChanged: boolean,
    PostChanged: boolean,
    TagsChanged: boolean,
    Time: string
    Worker: string
}

type ConnectedItem = {
    Id: number,
    Name: string
}

export type ClientInfo = {
    Id: number,
    Address: string[],
    AnalyticsLists: AnalyticsListItem[],
    Changelog: ChangelogItem[],
    ConnectedClients: ConnectedItem[],
    ConnectedProjects: ConnectedItem[],
    Contacts: string,
    CreateTime: string,
    Creator: string,
    HasAccessToAnalytics: boolean,
    Name: string
    Post: string
    Tags: string[]
}

export type NewContact = {
    Name: string,
    Post: string,
    Contacts: string,
    InputContainer: {
        components: AddressNode[],
        activeLevel: number
    },
    Tags: string[],
    ClientId?: number
}

type AddressNode = {
    addressNodeId: number,
    level: number,
    value: string
}

export const clientsStore = defineStore({
    id: 'clientsStore',
    state: () => ({
        isClientsLoading: true,
        isClientsPartialLoading: false,
        canLoadNext: true,
        clients: [],
        editedClient: null
    } as ContactsState),
    actions: {
        async fetchClients(request: ClientRequestData) {
            try {
                if (request.toSkip === 0) {
                    this.canLoadNext = true
                }

                if (this.canLoadNext) {
                    if (request.toSkip === 0) {
                        this.isClientsLoading = true;
                    } else {
                        this.isClientsPartialLoading = true;
                    }

                    const clients = await PersonApi.searchPerson(request);
                    if (request.toSkip === 0) {
                        this.clients = clients;
                    } else {
                        this.clients.push(...clients);
                    }

                    if (!clients.length || clients.length < request.count) {
                        this.canLoadNext = false
                    }
                }
            } catch (error) {
                console.error(error);
            } finally {
                this.isClientsLoading = false;
                this.isClientsPartialLoading = false;
            }
        },
        setEditedClient(contact: Client | null): void {
            this.editedClient = contact;
        },
        async createContact(contact: NewContact) {
            try {
                this.isClientsLoading = true
                const newContact = await PersonApi.createContact(contact)
                this.clients.unshift(newContact)
                return newContact
            } catch (e) {
                console.error(e)
            } finally {
                this.isClientsLoading = false
            }
        },
        async updateContact(contact: Client, payload: NewContact & { PersonId: number }) {
            try {
                this.isClientsLoading = true
                const updatedContact = await PersonApi.updateContact(payload)
                Object.assign(contact, updatedContact)
            } catch (e) {
                console.error(e)
            } finally {
                this.isClientsLoading = false
            }
        },
        async deleteClient(clientId: number) {
            try {
                this.isClientsLoading = true
                PersonApi.deleteContact(clientId);
                const idx = this.clients.findIndex(client => client.Id === clientId)
                if (idx !== -1) {
                    this.clients.splice(idx, 1);
                }
            } catch (e) {
                console.error(e)
            } finally {
                this.isClientsLoading = false
            }
        },
        async attachClientToContact(contact: Client, clientId: number) {
            try {
                this.editedClient = contact.Id
                contact.Clients = await PersonApi.attachClient(contact.Id, clientId)
            } catch (e) {
                console.error(e)
            } finally {
                this.editedClient = null
            }
        },
        async detachClientFromContact(contact: Client, clientId: number) {
            try {
                this.editedClient = contact.Id
                const modifiedClients = await PersonApi.detachClient(contact.Id, clientId)
                if (modifiedClients) {
                    contact.Clients = modifiedClients;
                }
            } catch (e) {
                console.error(e)
            } finally {
                this.editedClient = null
            }
        },
        async detachAllClientsFromContact(contact: Client, clientIds: number[], lastExistClientId: number) {
            try {
                this.editedClient = contact.Id
                await Promise.allSettled(clientIds.map(id => PersonApi.detachClientPromise(contact.Id, id)))
                contact.Clients = contact.Clients.filter(c => c.Id === lastExistClientId)
            } catch (e) {
                console.error(e)
            } finally {
                this.editedClient = null
            }
        },
        async attachTagToContact(contact: Client, tagName: string) {
            try {
                const updatedTags = await TagsApi.addTag({
                    EditingTagId: 0,
                    Name: tagName,
                    EntityType: TagEntityType.Person,
                    EntityId: contact.Id,
                })
                if (!Array.isArray(updatedTags)) return

                contact.Tags = updatedTags as unknown as Tag[]
            } catch (e) {
                console.error(e)
            }
        },
        async detachTagFromContact(contact: Client, tagId: number) {
            try {
                const ok = await TagsApi.deleteTag({
                    TagId: tagId,
                    EntityType: TagEntityType.Person,
                    EntityId: contact.Id,
                })
                if (!ok) return

                contact.Tags = (contact.Tags ?? []).filter(t => t.Id !== tagId)
            } catch (e) {
                console.error(e)
            }
        },
        async getExistContacts(contactIds: number[]) {
            try {
                this.isClientsLoading = true
                const contacts = await PersonApi.getContactsByIds(contactIds)

                if (contacts.length) {
                    this.clients = contacts
                }
            } catch (e) {
                console.error(e)
            } finally {
                this.isClientsLoading = false
            }
        }
    },
    getters: {
        clientsLoading(state): boolean {
            return state.isClientsLoading;
        },
        clientsPartialLoading(state): boolean {
            return state.isClientsPartialLoading;
        },
        getClients(state): Client[] {
            return state.clients;
        },
        getEditedClient(state): Client | number | null {
            return state.editedClient;
        }
    }
})
